import { z } from 'zod';
import {
  appCategorySchema,
  appFilingModeSchema,
  appHealthSchema,
  appIdSchema,
  appPromoLabelSchema,
  appStatusSchema,
  currencySchema,
  waybillBayanStatusSchema,
  waybillBlockerSchema,
  waybillTriggerSchema,
} from '@hero/schema';
import { API } from '..';

/**
 * Apps & integrations marketplace contract.
 *
 * The catalog itself is declared in code, one folder per app in `@hero/apps`,
 * and served through these endpoints with `name` / `description` localized at
 * request time from the `Accept-Language` header the SDK sends. Brand names
 * are proper nouns and are intentionally not translated; `categories` and
 * `status` are stable keys the frontend localizes via `@hero/lang`. `logo` is
 * a public path served by the dashboard (e.g. `/images/apps/snb-logo.svg`).
 *
 * Everything below the marketplace section talks to a real provider. Every one
 * of those endpoints is scoped to the caller's Org — the backend derives the
 * Org from the auth token and never accepts it from the client, so one tenant
 * can't read or act against another's provider account.
 */

/** A marketplace card as the dashboard sees it. */
export const appViewSchema = z.object({
  id: appIdSchema,
  /** Brand name (proper noun, not translated). */
  name: z.string(),
  /** One/two sentence description. */
  description: z.string(),
  /** Public logo path served by the dashboard. */
  logo: z.string(),
  categories: z.array(appCategorySchema),
  status: appStatusSchema,
  /** Promotional badge (`new`, `soon`, …). Omitted when the card has none. */
  label: appPromoLabelSchema.optional(),
  /**
   * Whether Hero holds a platform account for this app, so a tenant can use
   * it without registering with the provider itself.
   *
   * A server fact — it depends on which credentials this environment was
   * deployed with (`BAYAN_PLATFORM_CLIENT_ID` for Logisti), not on anything
   * about the caller. It lives on the catalog card rather than on the install
   * because it is the one thing a caller needs to know *before* it has an
   * install: without it, the connect flow can only offer the bring-your-own
   * -account path, and offering the other one would dead-end.
   */
  platformAccount: z.boolean().default(false),
});
export type AppView = z.infer<typeof appViewSchema>;

/** GET the app catalog. No request payload. */
export type AppGetAllAPI = API<undefined, AppView[]>;

/** POST "apply for early access" / register interest in an app. */
export const appApplyEarlyAccessAPISchema = z.object({
  /** Id of the app the user is interested in. */
  appId: z.string().min(1),
  /** Display name of the app, captured for convenience. */
  appName: z.string().optional(),
  /** Optional free-text note from the requester. */
  note: z.string().optional(),
});
export type AppApplyEarlyAccessReq = z.infer<typeof appApplyEarlyAccessAPISchema>;
export type AppApplyEarlyAccessAPI = API<AppApplyEarlyAccessReq, { success: boolean }>;

// ───────────────────────────────────────────────────────────────────────────
// Installs
// ───────────────────────────────────────────────────────────────────────────

/** Behaviour knobs an Org can change after installing Logisti. */
export const logistiSettingsAPISchema = z.object({
  trigger: waybillTriggerSchema.optional(),
  autoCloseOnDelivery: z.boolean().optional(),
  autoCancelOnOrderCancel: z.boolean().optional(),
  defaultGoodTypeId: z.number().int().positive().optional(),
  defaultPaymentMethodId: z.number().int().positive().optional(),
  deliverToClient: z.boolean().optional(),
  tradable: z.boolean().optional(),
  paidBySender: z.boolean().optional(),
});
export type LogistiSettingsReq = z.infer<typeof logistiSettingsAPISchema>;

/**
 * One tenant's install as the dashboard sees it.
 *
 * `accountIdMasked` is the whole reason this is an explicit allowlist rather
 * than the raw document: the tenant already knows its own account id and can
 * re-read it from the provider's portal, so echoing it back in full would put
 * it in browser history, logs, and screenshots for no benefit.
 *
 * ## Why `canIssue` is here rather than on its own endpoint
 *
 * There used to be a `GET /apps/logisti/capability` answering exactly this,
 * because platform filing wrote no install row and so this list could not.
 * Once every filing mode writes a row, the two were one fact behind two URLs —
 * and the surfaces that read the wrong one showed a Connect button to someone
 * who was already issuing. It is server-derived rather than left to the client
 * so the rule (`enabled`, and the identity the mode needs) lives in one place.
 */
export const appInstalledViewSchema = z.object({
  _id: z.string(),
  appId: appIdSchema,
  enabled: z.boolean(),
  health: appHealthSchema,
  /** Which licence files — see `appFilingModeSchema` for what each requires. */
  filingMode: appFilingModeSchema,
  /**
   * Whether this tenant can file right now.
   *
   * `enabled` AND the identity its `filingMode` needs: an own-account row
   * needs verified credentials, a platform row needs the org's CR, an
   * individual row needs the driver's id and its issue number. A row can
   * exist and still be `false` — a tenant that has connected but not yet
   * supplied its CR is the ordinary case.
   */
  canIssue: z.boolean(),
  /** Masked, e.g. `••••3f9a`. Absent when uninstalled. */
  accountIdMasked: z.string().optional(),
  settings: logistiSettingsAPISchema.optional(),
  verifiedAt: z.coerce.date().optional(),
  lastSyncAt: z.coerce.date().optional(),
  lastError: z.string().optional(),
});
export type AppInstalledView = z.infer<typeof appInstalledViewSchema>;

/** GET the caller's installs. No request payload. */
export type AppInstalledGetAllAPI = API<undefined, AppInstalledView[]>;

/**
 * POST connect Logisti, in whichever mode the tenant qualifies for.
 *
 * ## `own_account`
 *
 * The tenant must already be registered in Bayan with a valid NAQL license —
 * Hero cannot create either on its behalf. The backend verifies `accountId`
 * with a live provider call before saving, so nobody sits on a broken install
 * believing it works.
 *
 * ## `platform` / `individual`
 *
 * Hero's own licence files, so there is no `accountId` to verify and none is
 * accepted. What the backend checks instead is the identity the document will
 * declare: the unified establishment number (10 digits starting with 7) for
 * both `platform` (Org) and `individual` (Driver, as هوية صاحب العمل), plus
 * the driver's id and issue number for `individual`. Either can be supplied
 * in the same request that connects — `crNumber` is written to
 * `legal.registrationId` on the owner, `driverIdentity` to the Driver — so
 * the connect screen is never asking for something it cannot accept.
 *
 * One request shape for all three because they all end at the same single row
 * — a tenant on `platform` who later gets its own `client_id` posts here again
 * with `filingMode: 'own_account'` and the row is upgraded in place.
 */
export const appLogistiInstallAPISchema = z
  .object({
    /**
     * Which licence should file. Defaults to `own_account` so a client that
     * predates the other modes keeps its old meaning.
     */
    filingMode: appFilingModeSchema.default('own_account'),
    /**
     * The `client_id` from the tenant's Bayan portal profile page.
     *
     * Required for `own_account` (enforced by the refinement below), absent
     * for the modes that file on Hero's licence.
     */
    accountId: z.string().trim().min(1).optional(),
    /**
     * The unified establishment number (الرقم الموحد / هوية صاحب العمل),
     * persisted to `legal.registrationId` on the owner and sent to Bayan as
     * `carrier.moi` on every waybill. Must be 10 digits starting with `7`.
     *
     * Optional in the payload only so a tenant that already has a valid one
     * on file does not have to retype it. The backend rejects the request
     * when neither the payload nor the owner carries a value, or when the
     * value does not start with 7. `individual` is not exempt.
     */
    crNumber: z.string().trim().min(1).optional(),
    /**
     * The owner-operator's national id / iqama and the version number on the
     * card, persisted to `Driver.legal` and sent to Bayan as `driver.identityNumber`
     * / `issueNumber`. Required alongside `crNumber` (هوية صاحب العمل) for
     * `individual` filing.
     */
    driverIdentity: z
      .object({
        id: z.string().trim().min(1),
        idIssueNumber: z.number().int().positive(),
      })
      .optional(),
    /** Optional initial settings; defaults apply when omitted. */
    settings: logistiSettingsAPISchema.optional(),
  })
  .refine(info => info.filingMode !== 'own_account' || !!info.accountId, {
    message: 'accountId is required when connecting your own Bayan account',
    path: ['accountId'],
  });
export type AppLogistiInstallReq = z.infer<typeof appLogistiInstallAPISchema>;
export type AppLogistiInstallAPI = API<AppLogistiInstallReq, AppInstalledView>;

/**
 * DELETE disconnect. Sets `enabled: false` and keeps the row — credentials
 * included, so reconnecting is one click rather than another round through the
 * Bayan portal, and the waybills already filed still point at a live install id.
 * A disabled row is a hard stop: nothing is filed for it, not even on Hero's
 * licence.
 */
export type AppLogistiUninstallAPI = API<undefined, { success: boolean }>;

/** PATCH settings. */
export type AppLogistiUpdateSettingsAPI = API<LogistiSettingsReq, AppInstalledView>;

/**
 * One cargo type from Bayan's taxonomy, as a picker needs it.
 *
 * Both names come straight from ELM, which is why nothing here goes through
 * `@hero/lang`: this is a government catalogue of ~160 entries that changes on
 * ELM's schedule, and a hand-maintained Hero translation of it would be a
 * second source of truth guaranteed to drift.
 */
export const bayanGoodTypeViewSchema = z.object({
  id: z.number().int().positive(),
  nameEnglish: z.string(),
  nameArabic: z.string(),
  /** Absent for a top-level type. Lets a picker narrow to one branch. */
  parentId: z.number().int().positive().optional(),
});
export type BayanGoodTypeView = z.infer<typeof bayanGoodTypeViewSchema>;

/**
 * GET the cargo types this Org may put on a waybill.
 *
 * Leaves only — Bayan rejects a parent node, so offering one in a dropdown
 * just produces a `good_type_not_a_leaf` blocker after the user has committed.
 */
export type AppLogistiGoodTypesAPI = API<undefined, BayanGoodTypeView[]>;

// ───────────────────────────────────────────────────────────────────────────
// Government waybill (Bayan)
// ───────────────────────────────────────────────────────────────────────────

/** Bayan-waybill state for one order. */
export const orderWaybillBayanStatusSchema = z.object({
  /** `false` when no Org party to this order has Logisti installed. */
  connected: z.boolean(),
  status: waybillBayanStatusSchema.optional(),
  /** String, not number — see `orderWaybillBayanSchema.tripId`. */
  tripId: z.coerce.string().optional(),
  waybillIds: z.array(z.number()).default([]),
  issuedAt: z.coerce.date().optional(),
  closedAt: z.coerce.date().optional(),
  canceledAt: z.coerce.date().optional(),
  lastError: z.string().optional(),
  /**
   * Whether `lastError` is one the retry sweep will actually replay.
   *
   * Only `unavailable` failures (5xx, timeout, DNS) are queued; a payload
   * rejection or a bad credential is terminal. Callers need the difference
   * because the honest sentence is different: "I'll retry automatically"
   * for the first, and something the user can act on for the second.
   * Saying the first for both is what left a Wathiqa Plus user waiting for a
   * document that was never coming back.
   */
  lastErrorRetryable: z.boolean().optional(),
  /**
   * Bayan never answered the create call (timeout, 500, 504) — it may have
   * filed the trip anyway, and Hero has no number to look it up by.
   *
   * Not retried automatically: a second create would be a second
   * government document. Staff check the Bayan portal and re-file from the
   * admin panel; the chat says that, instead of "I'll retry".
   */
  lastErrorOutcomeUnknown: z.boolean().optional(),
  /**
   * Cloudinary URL of the archived government PDF, when Hero has stored one.
   *
   * Present so the dashboard can open the وثيقة with a plain link — the same
   * affordance as Hero's own waybill — instead of round-tripping base64
   * through the API on every click. Absent on documents issued before
   * archiving existed; callers fall back to the PDF endpoint.
   */
  pdfUrl: z.string().optional(),
  /** Empty when the order is ready to issue. */
  blockers: z.array(waybillBlockerSchema).default([]),
  /** Name of the Org whose Bayan account issues (or would issue) this. */
  issuingOrgName: z.string().optional(),
  /**
   * The party the document **names** as the carrier — the one hauling.
   *
   * Deliberately distinct from {@link issuingOrgName}, which is whoever
   * files. They diverge in the two cases that matter: platform filing
   * (Hero's licence, the subscriber's CR) and a CRM carrier named on an
   * issue-only order. Surfaces that show "who is on this document"
   * must read this one — printing the filer there would put Hero on a
   * government form as a carrier, which it never is.
   */
  declaredCarrierName: z.string().optional(),
  /**
   * Whether Hero's own Bayan licence is filing this rather than the Org's.
   *
   * Drives the "connect your own account" affordance: an Org covered by
   * platform filing is `connected: true` and can issue today, so offering it
   * a connect button as if nothing worked would be wrong. It is still worth
   * telling it whose licence carries the document.
   */
  issuedByPlatform: z.boolean().optional(),
  /**
   * What the document WILL declare, straight off the mapped Bayan payload.
   * Present only when the order is ready to issue (no blockers, not issued).
   *
   * Read from the mapper rather than re-derived from the order so the
   * confirmation the user approves is the submission itself — the fare and
   * the cargo classification are the two values a dispatcher most often has
   * to correct, and finding out after the document is legally live means
   * cancelling and re-issuing it.
   */
  declaration: z
    .object({
      /** Transport fare, from the contract leg closest to the road. */
      fare: z.number().optional(),
      /** Currency of {@link fare} — the same leg's. */
      currency: currencySchema.optional(),
      /** Sum of the declared item weights, in kilograms. */
      totalWeightKg: z.number().optional(),
      /**
       * Declared value of the goods, summed across items, in {@link currency}.
       *
       * Read from Hero's own items rather than the Bayan payload: Bayan only
       * carries a per-item `price` when the account delivers to the client,
       * and the value is worth showing before filing either way — it is what
       * the goods are insured and claimed against.
       */
      itemsTotalCost: z.number().optional(),
      /** Sender name the document files — the user's named party, or the fallback. */
      senderName: z.string().optional(),
      /** Recipient name the document files — the user's named party, or the fallback. */
      recipientName: z.string().optional(),
      items: z
        .array(
          z.object({
            /** Hero's own item name, when the order names it. */
            label: z.string().optional(),
            /** Bayan taxonomy leaf being declared — both languages, caller picks. */
            goodTypeNameEn: z.string().optional(),
            goodTypeNameAr: z.string().optional(),
            quantity: z.number().optional(),
            weightKg: z.number().optional(),
            /** Declared value of this line, from Hero's item. */
            cost: z.number().optional(),
          }),
        )
        .default([]),
    })
    .optional(),
});
export type OrderWaybillBayanStatus = z.infer<typeof orderWaybillBayanStatusSchema>;

export const orderWaybillBayanAPISchema = z.object({
  /** `Order` `_id` hex string. */
  orderId: z.string().trim().min(1),
});
export type OrderWaybillBayanReq = z.infer<typeof orderWaybillBayanAPISchema>;

/** GET the Bayan-waybill state for an order (order panel + agent). */
export type AppGetOrderWaybillBayanAPI = API<OrderWaybillBayanReq, OrderWaybillBayanStatus>;

/**
 * POST issue (or retry) the government waybill for an order.
 *
 * Idempotent: an order that already carries a `tripId` returns its existing
 * state rather than creating a second trip. Concurrent callers are serialized
 * by a conditional update on `Order.waybillBayan` — exactly one can claim.
 */
export const appIssueWaybillBayanAPISchema = orderWaybillBayanAPISchema.extend({
  /**
   * Override the good type for every item on this order. Needed when Hero's
   * `ItemType` is too coarse for Bayan's taxonomy (construction, chemicals).
   */
  goodTypeId: z.number().int().positive().optional(),
});
export type AppIssueWaybillBayanReq = z.infer<typeof appIssueWaybillBayanAPISchema>;
export type AppIssueWaybillBayanAPI = API<AppIssueWaybillBayanReq, OrderWaybillBayanStatus>;

/** GET the Bayan-issued PDF for an order, as a base64 payload. */
export type AppGetWaybillBayanPdfAPI = API<
  OrderWaybillBayanReq,
  { fileName: string; contentType: string; base64: string }
>;
